import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import Drawer from '../../control/Drawer'
import useReportSurvey from '../../hooks/map/useReportSurvey'
import useUnblockPopupDetail from '../../hooks/map/useUnblockPopupDetail'
import UnblockPopupDetail from './UnblockPopupDetail'
import Dropdown from '../../control/Dropdown'
import Scrollbar from '../../control/Scrollbar'
import SplitButton from '../../control/SplitButton'
import ReportItemCard from '../../control/ReportItemCard'
import ImageGalleryControl from '../../control/ImageGallery'
import ReportItemDetailLabel from '../../control/ReportItemDetailLabel'
import { calculateMidFrequency } from '../../util/utilityFunction'
import Dialog from '../../control/Dialog'

type UseReportSurveyType = ReturnType<typeof useReportSurvey>

const FormReportSurvey = () => {
    const {
        drawer,
        dataReport,
        dataReportDetail,
        month,
        year,
        dataYear,
        dataMonth,
        onChangeYear,
        onChangeMonth,
        handleClickLocation,
        handleOpenDrawerMain,
        options,
        sort,
        dataSort,
        handleSortData,
        getDataSort,
        isIos,
        openDialogError,
        handleCloseErrorDialog,
    } = useReportSurvey() as UseReportSurveyType
    const { openIosHelp, onClickOpenIosHelp, onClickCloseIosHelp } = useUnblockPopupDetail()
    const { t } = useTranslation()
    const classes = useStyles({
        dataReportDetail: drawer.openDrawerDetail,
        dataReportImage: dataReportDetail?.pictures?.length,
        isIos,
    })

    let images: Array<ImageGalleryItemType> = []
    let reception = '-'
    let signalState = '-'
    let receivingPoint = '-'
    if (dataReportDetail) {
        if (dataReportDetail.pictures) {
            dataReportDetail.pictures.forEach((item) => {
                images.push({
                    original: item,
                    thumbnail: item,
                })
            })
        }

        if (dataReportDetail.receptionMode) {
            reception = dataReportDetail.receptionMode.name
        }
        if (dataReportDetail.signalState) {
            signalState = dataReportDetail.signalState.name
        }
        if (dataReportDetail.receivingPoint) {
            receivingPoint = `${dataReportDetail.receivingPoint.latitude},${dataReportDetail.receivingPoint.longitude}`
        }
    }

    const DrawerFormReportSurvey = (
        <Drawer
            isOpen={drawer.openDrawer}
            hideBackdrop
            inlineStyle={{ position: 'unset' }}
            overrideClass={{ paper: classes.drawerPaper }}
        >
            <StyledContainer>
                <StyledTitle>
                    <Link to="/">
                        <IconButton
                            aria-label="close"
                            onClick={handleOpenDrawerMain}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 15,
                            }}
                            className={classes.backIcon}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Link>
                    <StyledTypography variant="h6">{t('MENU.REPORT_SURVEY')}</StyledTypography>
                </StyledTitle>
                <StyledContent>
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={2}>
                            <Dropdown
                                label={t('LABEL.MAP.REPORT_COMMON.YEAR')}
                                value={year}
                                options={dataYear}
                                onChange={onChangeYear}
                                customStyle={{ width: 150 }}
                            />
                            <Dropdown
                                label={t('LABEL.MAP.REPORT_COMMON.MONTH')}
                                value={month}
                                options={dataMonth}
                                onChange={onChangeMonth}
                                customStyle={{ width: 150 }}
                            />
                        </Stack>
                        <Stack direction="row" justifyContent="flex-start" sx={{ paddingTop: '8px' }}>
                            <SplitButton
                                disabled={dataReport.length === 0}
                                optionsDisplay={0}
                                options={options.options}
                                onClick={options.onClickOption}
                                onMenuItemClick={options.onMenuItemClick}
                            />
                        </Stack>

                        {isIos && (
                            <Stack direction="row" alignItems="center">
                                <IconButton onClick={onClickOpenIosHelp}>
                                    <StyledIconInfo />
                                </IconButton>
                                <StyledTypoIosRemark>
                                    {'กรุณา Unblock Pop-up สำหรับผู้ใช้งาน iOS Safari'}
                                </StyledTypoIosRemark>
                            </Stack>
                        )}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ paddingTop: isIos ? '0px' : '8px' }}
                        >
                            <Typography>{t('LABEL.MAP.REPORT_FEEDBACK.SORT')}</Typography>
                            <Dropdown
                                value={sort}
                                options={dataSort}
                                onChange={handleSortData}
                                customStyle={{ width: 200 }}
                            />
                        </Stack>
                    </Stack>
                </StyledContent>
                <StyledContentReport className={classes.contentReport}>
                    <div style={{ width: '100%', height: '100%' }}>
                        <Scrollbar
                            scrollbarTrackBorderRadius={10}
                            scrollbarThumbBorderRadius={10}
                            style={{ height: 'calc(100% - 160px)' }}
                        >
                            {getDataSort().length > 0 ? (
                                <Stack spacing={2} sx={{ px: 2, py: 1 }}>
                                    {getDataSort().map((item, index) => (
                                        <ReportItemCard
                                            key={index}
                                            item={item}
                                            type={'survey'}
                                            onClickLocation={handleClickLocation}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <div style={{ width: '100%', textAlign: 'center' }}>
                                    <Typography>{'ไม่มีข้อมูล'}</Typography>
                                </div>
                            )}
                        </Scrollbar>
                    </div>
                </StyledContentReport>
            </StyledContainer>
        </Drawer>
    )

    const DrawerFormReportSurveyDetail = (
        <Drawer
            isOpen={drawer.openDrawerDetail}
            hideBackdrop
            inlineStyle={{ position: 'unset' }}
            overrideClass={{ paper: classes.drawerPaper }}
        >
            <StyledContainer>
                <StyledTitle>
                    <Link to="/report-survey">
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                drawer.setOpenDrawerDetail(false)
                            }}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 15,
                            }}
                            className={classes.backIcon}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Link>
                    <StyledTypography variant="body2">{t('MENU.REPORT_SURVEY_DETAIL')}</StyledTypography>
                </StyledTitle>
                <StyledContentReport className={classes.contentReport}>
                    <div style={{ width: '100%', height: '100%' }}>
                        {images.length > 0 ? (
                            <div style={{ backgroundColor: '#c0c0c0' }}>
                                <ImageGalleryControl
                                    data={images}
                                    sxProps={{ width: '100%' }}
                                    gridSize={{ xs: 12 }}
                                    showBullets
                                    showNav
                                    useFullScreen
                                    fullScreenSize="xs"
                                />
                            </div>
                        ) : null}

                        <Scrollbar scrollbarTrackBorderRadius={10} scrollbarThumbBorderRadius={10}>
                            {dataReportDetail ? (
                                <Stack spacing={1} style={{ margin: 10 }}>
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.RECEPTION')}
                                        text={reception}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.HEIGHT')}
                                        text={dataReportDetail.height}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.FREQUENCY')}
                                        text={
                                            dataReportDetail.frequency
                                                ? `${dataReportDetail.frequency} (${t(
                                                      'LABEL.MAP.STATION.MID_FREQUENCY',
                                                  )} ${calculateMidFrequency(dataReportDetail.frequency)} ${t(
                                                      'LABEL.MAP.STATION.MID_FREQUENCY_UNIT',
                                                  )})`
                                                : '-'
                                        }
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.INPUT_VOLTAGE')}
                                        text={dataReportDetail.inputVoltage}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.STRENGTH')}
                                        text={dataReportDetail.strength}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.CN')}
                                        text={dataReportDetail.cn}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.MER')}
                                        text={dataReportDetail.mer}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.LINK_MARGIN')}
                                        text={dataReportDetail.linkMargin}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.BER_BEFORE')}
                                        text={dataReportDetail.berBefore}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.BER_AFTER')}
                                        text={dataReportDetail.berAfter}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.LDPC')}
                                        text={dataReportDetail.ldpc}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.SIGNALSTATE_DESC')}
                                        text={signalState}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.SIGNAL_DETAIL')}
                                        text={dataReportDetail.signalDetail}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.BUILDING_NM')}
                                        text={dataReportDetail.buildingName}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.BUILDING_DESC')}
                                        text={dataReportDetail.buildingDesc}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.PHYSICAL_DESC')}
                                        text={dataReportDetail.physicalDesc}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.OTHER_DESC')}
                                        text={dataReportDetail.otherDesc}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.PROVINCE')}
                                        text={dataReportDetail.province}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.AMPHUR')}
                                        text={dataReportDetail.amphur}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.TAMBOL')}
                                        text={dataReportDetail.tambol}
                                    />
                                    <ReportItemDetailLabel
                                        label={t('LABEL.MAP.REPORT_SURVEY.RECEIVING_POINT')}
                                        text={receivingPoint}
                                    />
                                </Stack>
                            ) : null}
                        </Scrollbar>
                    </div>
                </StyledContentReport>
            </StyledContainer>
        </Drawer>
    )

    return (
        <React.Fragment>
            {DrawerFormReportSurvey}
            {DrawerFormReportSurveyDetail}
            <UnblockPopupDetail isOpen={openIosHelp} onClose={onClickCloseIosHelp} />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={t('ALERT.CONTENT.NOT_RETRIEVE_DATA')}
                isOpen={openDialogError}
                onClickOk={handleCloseErrorDialog}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </React.Fragment>
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        overflow: 'hidden !important',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    backIcon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'end',
        paddingBottom: '15px',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
    },
    link: {
        color: '#2367b4',
        textDecoration: 'underline',
        '&:hover': {
            color: '#1d5494',
            cursor: 'pointer',
        },
    },
    textDetailHead: {
        color: '#000',
        fontSize: '18px',
        fontWeight: 600,
    },
    textDetailBody: {
        color: '#0F192C',
        fontSize: '16px',
        fontWeight: 400,
    },
    contentReport: {
        display: 'flex',
        height: (props: any) =>
            `calc(100% - ${props.dataReportDetail ? (props.dataReportImage ? 363 : 63) : props.isIos ? 154 : 123}px)`,
    },
}))

const StyledContainer = styled('div')(({ theme }) => ({
    width: '350px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}))

const StyledTitle = styled('div')(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: theme.palette.getContrastText('shadow-bottom'),
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
}))

const StyledContent = styled('div')(({ theme }) => ({
    display: 'flex',
    margin: '20px',
}))

const StyledContentReport = styled('div')(({ theme }) => ({}))

const StyledIconInfo = styled(InfoIcon)(({ theme }) => ({
    fontSize: '15px',
}))

const StyledTypoIosRemark = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
}))

export default observer(FormReportSurvey)
